import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/button';
import { CheckboxList } from './checkbox-list';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { GTINFilterActionSheetRoute, GTINFilterList } from './gtin-filter-list';
import { GTINFilters } from '@/hooks/use-gtin-metadata-filters';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { VariantPackInfoContext_GroupBadgeFieldsFragment } from '@/types';

interface Props {
  badges: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  badgesFilter: Set<string>;
  gtinFilters: GTINFilters;
  onClose: () => void;
  onSelectBadges: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const GTINFilterActionSheet: React.FC<Props> = ({
  badges,
  gtinFilters,
  onClose,
  onSelectBadges,
  badgesFilter,
}) => {
  const { height } = useWindowDimensions();

  const [activeListFilterIndex, setActiveListFilterIndex] = useState<
    number | null
  >(null);

  useEffect(() => {
    gtinFilters.stageAllFilters();
    // only run on mount, avoid running when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CombinedActionSheet
      contentStyle={[styles.paddingReset, { height: height * 0.9 }]}
      initialRoute={GTINFilterActionSheetRoute.Base}
      onClose={() => {
        gtinFilters.applyAllStagedFilters();
        onClose();
      }}
      onRouteChange={(routeKey) => {
        if (routeKey === GTINFilterActionSheetRoute.Base) {
          setActiveListFilterIndex(null);
        }
      }}
      renderScene={({ routeKey, jumpTo }) => {
        const buttonComponents = (
          <View style={styles.buttonWrapper}>
            <View style={styles.fullHeight}>
              <Button
                onPress={() => {
                  gtinFilters.resetAllStagedFilters();
                  gtinFilters.resetFilters();
                  onSelectBadges(new Set());
                  onClose();
                }}
                trackingData={null}
                type="tertiary"
              >
                {i18n.t('clear_filters')}
              </Button>
            </View>
            <View style={styles.fullHeight}>
              <Button
                onPress={() => {
                  gtinFilters.resetAllStagedFilters();
                  onClose();
                }}
                trackingData={null}
              >
                {i18n.t('apply')}
              </Button>
            </View>
          </View>
        );
        switch (routeKey) {
          case GTINFilterActionSheetRoute.Base:
            return (
              <View style={styles.fullHeight}>
                <GTINFilterList
                  badges={badges}
                  badgesFilter={badgesFilter}
                  gtinFilters={gtinFilters}
                  onBadgeFilterSelect={() => {
                    jumpTo(GTINFilterActionSheetRoute.BadgesFilter);
                  }}
                  onListFilterSelect={(listFilterIndex) => {
                    setActiveListFilterIndex(listFilterIndex);
                    jumpTo(GTINFilterActionSheetRoute.ListFilter);
                  }}
                />
                {buttonComponents}
              </View>
            );
          case GTINFilterActionSheetRoute.ListFilter:
            if (typeof activeListFilterIndex !== 'number') {
              return null;
            }
            const selectedFilter =
              gtinFilters.listFilters[activeListFilterIndex];
            if (!selectedFilter) {
              return null;
            }

            return (
              <View style={styles.fullHeight}>
                <Text style={[TYPOGRAPHY.h2, styles.filterTitle]}>
                  {selectedFilter.label}
                </Text>
                <CheckboxList
                  options={selectedFilter.options}
                  scrollEnabled
                  selectedValues={selectedFilter.filter}
                  setSelectedValues={selectedFilter.updateFilter}
                />
                {buttonComponents}
              </View>
            );
          case GTINFilterActionSheetRoute.BadgesFilter:
            return (
              <View style={styles.fullHeight}>
                <Text style={[TYPOGRAPHY.h2, styles.filterTitle]}>
                  {i18n.t('badges')}
                </Text>
                <CheckboxList
                  options={badges.map((b) => ({
                    title: b.name,
                    value: b.uuid,
                  }))}
                  scrollEnabled
                  selectedValues={badgesFilter}
                  setSelectedValues={onSelectBadges}
                />
                {buttonComponents}
              </View>
            );
          default:
            return null;
        }
      }}
      routes={{
        [GTINFilterActionSheetRoute.Base]: {
          previousKey: null,
          showSmallLayoutTitle: true,
          title: i18n.t('filters'),
        },
        [GTINFilterActionSheetRoute.ListFilter]: {
          previousKey: GTINFilterActionSheetRoute.Base,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: false,
        },
        [GTINFilterActionSheetRoute.BadgesFilter]: {
          previousKey: GTINFilterActionSheetRoute.Base,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: false,
        },
      }}
      titleStyle={styles.title}
    />
  );
};

const styles = StyleSheet.create({
  buttonWrapper: {
    flexDirection: 'row',
    marginHorizontal: SCREEN_PADDING,
  },
  filterTitle: {
    marginBottom: 16,
    marginLeft: SCREEN_PADDING,
  },
  fullHeight: {
    flex: 1,
  },
  paddingReset: {
    paddingHorizontal: 0,
  },
  title: {
    marginLeft: SCREEN_PADDING,
  },
});
